<template>
    <div class="min-h-screen text-left">
        <!-- Main Content -->
        <div class="max-w-7xl mx-auto px-6 py-8">
            <!-- Profile Card -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                <div class="px-8 py-6">
                    <div class="flex items-start space-x-6">
                        <!-- Profile Picture -->
                        <div class="relative">
                            <img :src="profilePicture(user.profile_picture_url)" :alt="user.name"
                                class="w-20 h-20 rounded-xl object-cover border-2 border-gray-100 shadow-sm" />
                            <div
                                class="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white">
                            </div>
                        </div>

                        <!-- User Info -->
                        <div class="flex-1">
                            <div class="flex items-start justify-between">
                                <div>
                                    <h2 class="text-2xl font-bold text-gray-900 mb-1">{{ user.name }}</h2>
                                    <p class="text-gray-600 mb-2">{{ user.email }}</p>
                                    <div class="flex items-center space-x-4">
                                        <span v-if="user.position" class="text-sm text-gray-500">
                                            <font-awesome-icon :icon="['fas', 'briefcase']" class="mr-1" />
                                            {{ user.position }}
                                        </span>
                                        <span v-if="user.role_display_name || user.role_name"
                                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {{ user.role_display_name || user.role_name }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Entity Logo -->
                                <div v-if="user.entity"
                                    class="flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-lg">
                                    <img :src="entityLogo(user.entity.split(',')[0])"
                                        :alt="user.entity ? user.entity.split(',')[0] : 'Entity'"
                                        class="w-8 h-8 object-contain" />
                                    <span class="text-sm font-medium text-gray-700">{{ getEntityName(user.entity)
                                        }}</span>
                                </div>
                                <!-- Impersonate button (Super Admin only) -->
                                <button v-if="isSuperAdmin" @click="$emit('impersonate-user', user)"
                                    class="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                    title="Switch to this user">
                                    <font-awesome-icon :icon="['fas', 'user-shield']" />
                                    Impersonate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Information Grid -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <!-- Basic Information -->
                <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                            <font-awesome-icon :icon="['fas', 'user']" class="mr-3 text-blue-600" />
                            Basic Information
                        </h3>
                    </div>
                    <div class="p-6">
                        <div class="space-y-4">
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Full Name</span>
                                <span class="text-sm text-gray-900 font-medium">{{ user.name || '—' }}</span>
                            </div>
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Email</span>
                                <span class="text-sm text-gray-900 font-medium">{{ user.email || '—' }}</span>
                            </div>
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Phone Number</span>
                                <span class="text-sm text-gray-900 font-medium">{{ user.phone_number || '—' }}</span>
                            </div>
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Position</span>
                                <span class="text-sm text-gray-900 font-medium">{{ user.position || '—' }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Organization Information -->
                <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                            <font-awesome-icon :icon="['fas', 'building']" class="mr-3 text-green-600" />
                            Role & Organization
                        </h3>
                    </div>
                    <div class="p-6">
                        <div class="space-y-4">
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Role</span>
                                <span
                                    class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {{ user.role_display_name || user.role_name || '—' }}
                                </span>
                            </div>
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Entity</span>
                                <div class="flex items-center space-x-2">
                                    <img :src="entityLogo(user.entity)"
                                        :alt="user.entity ? user.entity.split(',')[0] : 'Entity'"
                                        class="w-5 h-5 object-contain" />
                                    <span class="text-sm text-gray-900 font-medium">{{ getEntityName(user.entity) || '—'
                                        }}</span>
                                </div>
                            </div>
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Campus</span>
                                <span class="text-sm text-gray-900 font-medium">{{ user.campuses || '—' }}</span>
                            </div>
                            <div
                                class="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                                <span class="text-sm font-medium text-gray-500">Department</span>
                                <span class="text-sm text-gray-900 font-medium">
                                    {{ user.userDepartments && user.userDepartments.length > 0 ?
                                        user.userDepartments[0].department : '—' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- User Activities -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                        <font-awesome-icon :icon="['fas', 'chart-line']" class="mr-3 text-indigo-600" />
                        User Activities
                    </h3>
                </div>
                <div class="p-6">
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <!-- Think Tank Section (Original Gradient Card) -->
                        <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 flex flex-col">
                            <div class="flex items-center justify-between mb-4">
                                <div class="flex items-center space-x-3">
                                    <h3 class="text-lg font-semibold text-blue-800 flex items-center">
                                        <font-awesome-icon :icon="['fas', 'lightbulb']" class="mr-2" />
                                        Think Tank
                                    </h3>
                                </div>
                                <span v-if="!isLoading"
                                    class="flex-shrink-0 bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {{ thinkTankCount }} {{ thinkTankCount > 1 ? 'posts' : 'post' }}
                                </span>
                                <div v-else class="bg-blue-200 px-3 py-1 rounded-full text-sm">
                                    <div class="shimmer h-4 w-12"></div>
                                </div>
                            </div>

                            <!-- Loading Shimmer -->
                            <div v-if="isLoading" class="space-y-3 flex-auto flex flex-col">
                                <div v-for="n in 3" :key="n" class="bg-white rounded-lg p-3 shadow-sm">
                                    <div class="shimmer h-4 w-full mb-2"></div>
                                    <div class="shimmer h-3 w-3/4"></div>
                                </div>
                            </div>

                            <!-- Content -->
                            <div v-else-if="thinkTankCount > 0" class="space-y-3 flex-auto flex flex-col">
                                <div v-for="thinkTank in recentThinkTanks" :key="thinkTank.id"
                                    class="bg-white rounded-lg p-3 shadow-sm">
                                    <p class="text-sm text-gray-700 line-clamp-2">{{ thinkTank.short_description ||
                                        thinkTank.description }}</p>
                                    <p class="text-xs text-gray-500 mt-1">{{ formatDate(thinkTank.created_at) }}</p>
                                </div>
                                <div class="flex-auto flex items-end">
                                    <button v-if="thinkTankCount > 3" @click="viewAllThinkTanks"
                                        class="w-full mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                                        View All {{ thinkTankCount }} Posts
                                    </button>
                                </div>
                            </div>
                            <div v-else class="flex-auto flex flex-col justify-center itemse-center text-center py-4">
                                <p class="text-gray-500 text-sm">No think tank posts yet</p>
                            </div>
                        </div>

                        <!-- Academic Industry Engagement Hub Section (Original Gradient Card) -->
                        <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 flex flex-col">
                            <div class="flex items-center justify-between mb-4">
                                <h3 class="text-lg font-semibold text-green-800 flex items-center">
                                    <font-awesome-icon :icon="['fas', 'university']" class="mr-2" />
                                    Industry Engagement
                                </h3>
                                <span v-if="!isLoading"
                                    class="flex-shrink-0 bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {{ academicEngagementCount }} {{ academicEngagementCount > 1 ? 'posts' : 'post' }}
                                </span>
                                <div v-else class="bg-green-200 px-3 py-1 rounded-full text-sm">
                                    <div class="shimmer h-4 w-12"></div>
                                </div>
                            </div>

                            <!-- Loading Shimmer -->
                            <div v-if="isLoading" class="space-y-3 flex-auto flex flex-col">
                                <div v-for="n in 3" :key="n" class="bg-white rounded-lg p-3 shadow-sm">
                                    <div class="shimmer h-4 w-full mb-2"></div>
                                    <div class="shimmer h-3 w-3/4"></div>
                                </div>
                            </div>

                            <!-- Content -->
                            <div v-else-if="academicEngagementCount > 0" class="space-y-3 flex-auto flex flex-col">
                                <div v-for="engagement in recentAcademicEngagements" :key="engagement.id"
                                    class="bg-white rounded-lg p-3 shadow-sm">
                                    <p class="text-sm text-gray-700 font-medium">{{ engagement.title }}</p>
                                    <p class="text-xs text-gray-500 mt-1">{{ formatDate(engagement.created_at) }}</p>
                                </div>
                                <div class="flex-auto flex items-end">
                                    <button v-if="academicEngagementCount > 3" @click="viewAllAcademicEngagements"
                                        class="w-full mt-3 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                                        View All {{ academicEngagementCount }} Posts
                                    </button>
                                </div>
                            </div>
                            <div v-else class="flex-auto flex flex-col justify-center itemse-center text-center py-4">
                                <p class="text-gray-500 text-sm">No industry engagement posts yet</p>
                            </div>
                        </div>

                        <!-- Student Life Section (Original Gradient Card) -->
                        <div
                            class="flex-shrink-0 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 flex flex-col">
                            <div class="flex items-center justify-between mb-4">
                                <h3 class="text-lg font-semibold text-purple-800 flex items-center">
                                    <font-awesome-icon :icon="['fas', 'users']" class="mr-2" />
                                    Student Life
                                </h3>
                                <span v-if="!isLoading"
                                    class="bg-purple-200 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {{ extracurricularCount }} {{ extracurricularCount > 1 ? 'activities' : 'activity'
                                    }}
                                </span>
                                <div v-else class="bg-purple-200 px-3 py-1 rounded-full text-sm">
                                    <div class="shimmer h-4 w-12"></div>
                                </div>
                            </div>

                            <!-- Loading Shimmer -->
                            <div v-if="isLoading" class="space-y-3 flex-auto flex flex-col">
                                <div v-for="n in 3" :key="n" class="bg-white rounded-lg p-3 shadow-sm">
                                    <div class="shimmer h-4 w-full mb-2"></div>
                                    <div class="shimmer h-3 w-3/4"></div>
                                </div>
                            </div>

                            <!-- Content -->
                            <div v-else-if="extracurricularCount > 0" class="space-y-3 flex-auto flex flex-col">
                                <div v-for="activity in recentExtracurricularActivities" :key="activity.id"
                                    class="bg-white rounded-lg p-3 shadow-sm">
                                    <p class="text-sm text-gray-700 font-medium">{{ activity.title }}</p>
                                    <p class="text-xs text-gray-500 mt-1">{{ formatDate(activity.created_at) }}</p>
                                </div>
                                <div class="flex-auto flex items-end">
                                    <button v-if="extracurricularCount > 3" @click="viewAllExtracurricularActivities"
                                        class="w-full mt-3 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
                                        View All {{ extracurricularCount }} Activities
                                    </button>
                                </div>
                            </div>
                            <div v-else class="flex-auto flex flex-col justify-center itemse-center text-center py-4">
                                <p class="text-gray-500 text-sm">No activities yet</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Additional Information -->
            <div class="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                        <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-3 text-purple-600" />
                        Additional Information
                    </h3>
                </div>
                <div class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="bg-gray-50 rounded-lg p-4">
                            <h4 class="text-sm font-medium text-gray-700 mb-2">User ID</h4>
                            <p class="text-gray-900 font-mono text-sm">{{ user.id }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Activity Modals -->
    <base-modal v-if="showThinkTankModal" :title="`Think Tank Posts (${thinkTanks.length})`" height="70vh"
        @close="closeThinkTankModal">
        <ThinkTankModal :thinkTanks="thinkTanks" />
    </base-modal>

    <base-modal v-if="showAcademicEngagementModal" :title="`Industry Engagement Posts (${academicEngagementCount})`"
        height="70vh" @close="closeAcademicEngagementModal">
        <AcademicEngagementModal :academicEngagements="academicIndustryEngagements" />
    </base-modal>

    <base-modal v-if="showStudentLifeModal" :title="`Student Life Activities (${extracurricularCount})`" height="70vh"
        @close="closeStudentLifeModal">
        <StudentLifeModal :extracurricularActivities="extracurricularActivities" />
    </base-modal>

    <!-- Page Loading Overlay -->
    <div v-if="isPageLoading" class="h-full flex justify-center items-center">
        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
</template>

<script>
import gql from "graphql-tag";
import FCHSLogo from '@/assets/images/dashboard/alumni/FCHS.png';
import ADPOLYLogo from '@/assets/images/dashboard/alumni/ADPOLY.png';
import ADVETILogo from '@/assets/images/dashboard/alumni/ADVETI.png';
import ATSLogo from '@/assets/images/dashboard/alumni/ATS.png';
import ThinkTankModal from './components/ThinkTankModal.vue';
import AcademicEngagementModal from './components/AcademicEngagementModal.vue';
import StudentLifeModal from './components/StudentLifeModal.vue';

const GET_USER_ACTIVITIES_QUERY = gql`
    query GetUserActivities($userId: ID!, $limit: Int) {
        getUserActivities(userId: $userId, limit: $limit) {
            error
            error_code
            message
            data {
                thinkTanks {
                    id
                    short_description
                    description
                    created_at
                }
                academicIndustryEngagementHubs {
                    id
                    title
                    created_at
                }
                extracurricularActivities {
                    id
                    title
                    created_at
                }
                counts {
                    thinkTankCount
                    academicEngagementCount
                    extracurricularCount
                }
            }
        }
    }
`;

const GET_USER_THINK_TANKS = gql`
    query GetUserThinkTanks($userId: ID!, $limit: Int) {
        getUserThinkTanks(userId: $userId, limit: $limit) {
            error
            error_code
            message
            data {
                id
                short_description
                description
                created_at
            }
        }
    }
`;

const GET_USER_ACADEMIC_ENGAGEMENTS = gql`
    query GetUserAcademicEngagements($userId: ID!, $limit: Int) {
        getUserAcademicIndustryEngagementHubs(userId: $userId, limit: $limit) {
            error
            error_code
            message
            data {
                id
                title
                created_at
            }
        }
    }
`;

const GET_USER_EXTRACURRICULARS = gql`
    query GetUserExtracurriculars($userId: ID!, $limit: Int) {
        getUserExtracurricularActivities(userId: $userId, limit: $limit) {
            error
            error_code
            message
            data {
                id
                title
                created_at
            }
        }
    }
`;

export default {
    name: 'UserManagementView',
    components: {
        ThinkTankModal,
        AcademicEngagementModal,
        StudentLifeModal,
    },
    emits: ['close', 'impersonate-user'],
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            thinkTanks: [],
            academicIndustryEngagements: [],
            extracurricularActivities: [],
            thinkTankCount: 0,
            academicEngagementCount: 0,
            extracurricularCount: 0,
            isLoading: false,
            showThinkTankModal: false,
            showAcademicEngagementModal: false,
            showStudentLifeModal: false,
            isPageLoading: false,
        };
    },
    computed: {
        isSuperAdmin() {
            return this.$store.getters.isSuperAdmin;
        },
        recentThinkTanks() {
            return this.thinkTanks.slice(0, 3);
        },
        recentAcademicEngagements() {
            return this.academicIndustryEngagements.slice(0, 3);
        },
        recentExtracurricularActivities() {
            return this.extracurricularActivities.slice(0, 3);
        }
    },
    created() {
        this.loadUserActivities(3);
    },
    methods: {
        async loadUserActivities(limit) {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_ACTIVITIES_QUERY,
                    variables: (function (vm, l) {
                        const v = { userId: vm.user.id };
                        if (typeof l === 'number') { v.limit = l; }
                        return v;
                    })(this, limit),
                    fetchPolicy: 'network-only',
                });

                if (response.data.getUserActivities.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: response.data.getUserActivities.message,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                    return;
                }

                if (response.data.getUserActivities.data) {
                    this.thinkTanks = response.data.getUserActivities.data.thinkTanks || [];
                    this.academicIndustryEngagements = response.data.getUserActivities.data.academicIndustryEngagementHubs || [];
                    this.extracurricularActivities = response.data.getUserActivities.data.extracurricularActivities || [];

                    // Set total counts from backend
                    if (response.data.getUserActivities.data.counts) {
                        this.thinkTankCount = response.data.getUserActivities.data.counts.thinkTankCount || 0;
                        this.academicEngagementCount = response.data.getUserActivities.data.counts.academicEngagementCount || 0;
                        this.extracurricularCount = response.data.getUserActivities.data.counts.extracurricularCount || 0;
                    }
                }

            } catch (error) {
                console.error("Failed to load user activities:", error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load user activities. Please try again.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3B82F6',
                });
                // Set empty arrays on error
                this.thinkTanks = [];
                this.academicIndustryEngagements = [];
                this.extracurricularActivities = [];
                this.thinkTankCount = 0;
                this.academicEngagementCount = 0;
                this.extracurricularCount = 0;
            }
            this.isLoading = false;
        },
        profilePicture(url) {
            if (!url) {
                return '/img/default-avatar.png';
            }
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        entityLogo(entity) {
            const logos = {
                FCHS: FCHSLogo,
                ADPOLY: ADPOLYLogo,
                ADVETI: ADVETILogo,
                ATS: ATSLogo,
                ATHS: ATSLogo,
            };
            return logos[entity ? entity.split(',')[0] : ''] || '';
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        formatDate(dateString) {
            if (!dateString) return '—';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        },
        async viewAllThinkTanks() {
            this.isPageLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_THINK_TANKS,
                    variables: { userId: this.user.id },
                    fetchPolicy: 'network-only',
                });
                if (response.data.getUserThinkTanks.error) {
                    this.$swal({ icon: 'error', title: 'Error', text: response.data.getUserThinkTanks.message, confirmButtonText: 'OK', confirmButtonColor: '#3B82F6' });
                    return;
                }
                if (response.data.getUserThinkTanks.data) {
                    this.thinkTanks = response.data.getUserThinkTanks.data || [];
                }
            } catch (error) {
                console.error("Failed to load all think tanks:", error);
                this.$swal({ icon: 'error', title: 'Error', text: 'Failed to load think tank posts. Please try again.', confirmButtonText: 'OK', confirmButtonColor: '#3B82F6' });
            }
            this.isPageLoading = false;
            this.showThinkTankModal = true;
        },
        async viewAllAcademicEngagements() {
            this.isPageLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_ACADEMIC_ENGAGEMENTS,
                    variables: { userId: this.user.id },
                    fetchPolicy: 'network-only',
                });
                if (response.data.getUserAcademicIndustryEngagementHubs.error) {
                    this.$swal({ icon: 'error', title: 'Error', text: response.data.getUserAcademicIndustryEngagementHubs.message, confirmButtonText: 'OK', confirmButtonColor: '#3B82F6' });
                    return;
                }
                if (response.data.getUserAcademicIndustryEngagementHubs.data) {
                    this.academicIndustryEngagements = response.data.getUserAcademicIndustryEngagementHubs.data || [];
                }
            } catch (error) {
                console.error("Failed to load all academic engagements:", error);
                this.$swal({ icon: 'error', title: 'Error', text: 'Failed to load industry engagement posts. Please try again.', confirmButtonText: 'OK', confirmButtonColor: '#3B82F6' });
            }
            this.isPageLoading = false;
            this.showAcademicEngagementModal = true;
        },
        async viewAllExtracurricularActivities() {
            this.isPageLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_EXTRACURRICULARS,
                    variables: { userId: this.user.id },
                    fetchPolicy: 'network-only',
                });
                if (response.data.getUserExtracurricularActivities.error) {
                    this.$swal({ icon: 'error', title: 'Error', text: response.data.getUserExtracurricularActivities.message, confirmButtonText: 'OK', confirmButtonColor: '#3B82F6' });
                    return;
                }
                if (response.data.getUserExtracurricularActivities.data) {
                    this.extracurricularActivities = response.data.getUserExtracurricularActivities.data || [];
                }
            } catch (error) {
                console.error("Failed to load all extracurricular activities:", error);
                this.$swal({ icon: 'error', title: 'Error', text: 'Failed to load student life activities. Please try again.', confirmButtonText: 'OK', confirmButtonColor: '#3B82F6' });
            }
            this.isPageLoading = false;
            this.showStudentLifeModal = true;
        },
        closeThinkTankModal() {
            this.showThinkTankModal = false;
        },
        closeAcademicEngagementModal() {
            this.showAcademicEngagementModal = false;
        },
        closeStudentLifeModal() {
            this.showStudentLifeModal = false;
        },
        editUser() {
            this.$swal({
                icon: 'info',
                title: 'Coming Soon',
                text: 'User editing functionality will be implemented soon.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3B82F6',
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.shimmer {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
}

@keyframes shimmer {
    0% {
        background-position: -200% 0;
    }

    100% {
        background-position: 200% 0;
    }
}
</style>
